/* ==========================================================================
   Soon Maternity — Denim Size Guide popup
   Vanilla JS, no framework, no external dependency. Namespaced under
   "soon-dsg" / "soonDsg" throughout so nothing here can collide with the
   Broadcast theme or any app's scripts.

   Architecture note: every interaction (opening the popup, closing it, the
   unit toggle, the "Find your size" button, Enter-to-search, focus trap) is
   bound with ONE set of delegated listeners on `document`, guarded by
   `window.__soonDsgBound`. That means this file behaves correctly even if
   Shopify's Theme Editor reloads/re-renders the product section and this
   <script> tag re-executes: the guard stops a second set of listeners from
   ever being attached, and because the listeners are delegated (not bound
   to specific nodes) they keep working against whatever fresh DOM the
   reload produced, with no re-binding step required.
   ========================================================================== */
(function () {
  if (window.__soonDsgBound) return;
  window.__soonDsgBound = true;

  // ---- Style data -----------------------------------------------------
  // Inseam is fixed per style across all sizes; only Full Hip varies by
  // size (see SIZES below, shared across every style).
  var STYLES = [
    { key: 'relaxed-wide-leg', name: 'Relaxed Wide Leg Jeans', inseam: 78, desc: 'Our best-selling wide leg jean with a relaxed fit and signature over-belly stretch waistband, designed to grow with you.' },
    { key: 'heaven-straight', name: 'Heaven Straight Jeans', inseam: 80, desc: 'A classic straight leg jean with our signature mid to low-rise elastic waistband, designed for comfort through pregnancy and beyond.' },
    { key: 'heaven-straight-cropped', name: 'Heaven Straight Jeans (Cropped)', inseam: 74, desc: 'A cropped version of our classic straight leg jean, with our signature mid to low-rise elastic waistband for comfort through pregnancy and beyond.' },
    { key: 'soon-slim', name: 'Soon Slim Straight Leg Jeans', inseam: 80, desc: 'A slim straight-leg version of our best-selling Heaven Jeans, crafted in soft premium denim with our signature bump-friendly waistband for pregnancy and beyond.' },
    { key: 'soon-slim-cropped', name: 'Soon Slim Straight Leg Jeans (Cropped)', inseam: 74, desc: 'A cropped version of our Slim Straight Leg Jeans, crafted in soft premium denim with our signature bump-friendly waistband for pregnancy and beyond.' },
    { key: 'draped-drawstring', name: 'Draped Drawstring Jeans', inseam: 78, desc: 'A lightweight, relaxed jean crafted from 100% cotton, with an adjustable drawstring waistband designed for a flexible fit through pregnancy and beyond.' },
    { key: 'relaxed-wide-leg-drawstring', name: 'Relaxed Wide Leg Drawstring Jeans', inseam: 78, desc: 'A relaxed wide-leg jean with an adjustable drawstring waistband that grows with you, finished with authentic denim details for pregnancy and beyond.' },
    { key: 'high-rise-straight', name: 'High Rise Straight Leg Jeans', inseam: 78, desc: 'A high-rise straight-leg jean with our signature over-bump elastic waistband, designed for comfort through pregnancy and beyond.' },
    { key: 'straight-leg-overbelly', name: 'Straight Leg Overbelly Jeans', inseam: 80, desc: 'A full-length straight-leg jean in premium stretch denim, with our signature supportive over-belly waistband designed to stay comfortably in place.' },
    { key: 'milo-barrel', name: 'Milo Barrel Leg Jeans', inseam: 80, desc: 'A relaxed barrel-leg jean with our signature maternity waistband, combining a modern silhouette with authentic denim details for pregnancy and beyond.' },
    { key: 'relaxed-shorts', name: 'Relaxed Denim Shorts', inseam: 11, desc: 'A summer-ready version of our best-selling Relaxed Wide Leg Jeans, with a comfortable stretch waistband and relaxed fit for pregnancy and beyond.' },
    { key: 'heaven-shorts', name: 'Heaven Denim Shorts', inseam: 10, desc: 'A shorts version of our best-selling Heaven Straight Jeans, with a comfortable stretch waistband and timeless denim fit for pregnancy and beyond.' },
    { key: 'heaven-denim-long-shorts', name: 'Heaven Denim Long Shorts', inseam: 22, desc: 'A longer, more relaxed version of our best-selling Heaven Denim Shorts, designed for effortless comfort through pregnancy and beyond.' }
  ];

  // AU 6-18, seven rows, matching the theme's existing full Denim Fit Guide
  // page exactly. US/EU equivalents and full-hip ranges (cm) never vary by
  // style; only inseam does (see STYLES above).
  var SIZES = [
    { au: 6, us: 2, eu: 34, hip: [88, 93] },
    { au: 8, us: 4, eu: 36, hip: [93, 98] },
    { au: 10, us: 6, eu: 38, hip: [98, 103] },
    { au: 12, us: 8, eu: 40, hip: [103, 108] },
    { au: 14, us: 10, eu: 42, hip: [108, 113] },
    { au: 16, us: 12, eu: 44, hip: [113, 118] },
    { au: 18, us: 14, eu: 46, hip: [118, 123] }
  ];

  function toIn(cm) {
    var v = Math.round((cm / 2.54) * 2) / 2;
    return String(v).replace(/\.0$/, '');
  }

  function styleForKey(key) {
    var found = null;
    for (var i = 0; i < STYLES.length; i++) {
      if (STYLES[i].key === key) { found = STYLES[i]; break; }
    }
    if (!found) {
      // eslint-disable-next-line no-console
      console.warn('[soon-denim-size-guide] Unrecognised or missing style key "' + key + '" — falling back to "' + STYLES[0].key + '". Check this product\'s custom.denim_size_guide_style metafield.');
      found = STYLES[0];
    }
    return found;
  }

  // ---- Per-instance state, keyed by the popup's own element -----------
  // (root.__soonDsgState) so multiple product pages loaded in sequence by
  // the Theme Editor never bleed state into one another.
  function getState(root) {
    if (!root.__soonDsgState) {
      root.__soonDsgState = { unit: 'cm', submitted: false, lastFocused: null };
    }
    return root.__soonDsgState;
  }

  function renderChart(root) {
    var key = root.getAttribute('data-style-key') || '';
    var style = styleForKey(key);
    var state = getState(root);
    var unit = state.unit;

    var nameEl = root.querySelector('[data-soon-dsg-style-name]');
    var descEl = root.querySelector('[data-soon-dsg-style-desc]');
    if (nameEl) nameEl.textContent = style.name;
    if (descEl) descEl.textContent = style.desc;

    var body = root.querySelector('[data-soon-dsg-table-body]');
    if (!body) return;
    body.innerHTML = '';

    var hits = state.submitted ? matchSizes(root) : [];
    var hitAu = {};
    hits.forEach(function (h) { hitAu[h.au] = true; });

    SIZES.forEach(function (s) {
      var tr = document.createElement('tr');
      if (hitAu[s.au]) tr.className = 'is-match';

      var hipText = unit === 'cm'
        ? s.hip[0] + '–' + s.hip[1] + 'cm'
        : toIn(s.hip[0]) + '–' + toIn(s.hip[1]) + '"';
      var inseamText = unit === 'cm' ? style.inseam + 'cm' : toIn(style.inseam) + '"';

      [s.au, s.us, s.eu, hipText, inseamText].forEach(function (val) {
        var td = document.createElement('td');
        td.textContent = val;
        tr.appendChild(td);
      });
      body.appendChild(tr);
    });
  }

  function matchSizes(root) {
    var state = getState(root);
    var input = root.querySelector('[data-soon-dsg-hip]');
    if (!input) return [];
    var raw = parseFloat(input.value);
    if (!isFinite(raw) || raw <= 0) return [];
    var cm = state.unit === 'in' ? raw * 2.54 : raw;
    return SIZES.filter(function (s) { return cm >= s.hip[0] && cm <= s.hip[1]; });
  }

  function renderResult(root) {
    var state = getState(root);
    var resultEl = root.querySelector('[data-soon-dsg-result]');
    var kickerEl = root.querySelector('[data-soon-dsg-result-kicker]');
    var sizeEl = root.querySelector('[data-soon-dsg-result-size]');
    var noteEl = root.querySelector('[data-soon-dsg-result-note]');
    if (!resultEl) return;

    var input = root.querySelector('[data-soon-dsg-hip]');
    var raw = input ? parseFloat(input.value) : NaN;

    if (!state.submitted || !isFinite(raw) || raw <= 0) {
      resultEl.hidden = true;
      return;
    }

    var cm = state.unit === 'in' ? raw * 2.54 : raw;
    var hits = matchSizes(root);
    var kicker = '', size = '', note = '';

    if (hits.length === 0) {
      var small = cm < SIZES[0].hip[0];
      kicker = small ? 'Below our size range' : 'Above our size range';
      size = small ? 'AU 6' : 'AU 18';
      note = small
        ? 'AU 6 is our smallest denim size. Get in touch and we can help you find the right fit.'
        : 'AU 18 is our largest denim size. Get in touch and we can help you find the right fit.';
    } else if (hits.length === 1) {
      var h = hits[0];
      kicker = 'Your recommended size';
      size = 'AU ' + h.au + '  ·  US ' + h.us + '  ·  EU ' + h.eu;
      note = 'Highlighted in the chart below.';
    } else {
      kicker = 'You sit between two sizes';
      size = hits.map(function (h) { return 'AU ' + h.au; }).join(' or ');
      note = 'Choose the larger size for a looser, more relaxed fit, or the smaller size for a more tailored fit.';
    }

    if (kickerEl) kickerEl.textContent = kicker;
    if (sizeEl) sizeEl.textContent = size;
    if (noteEl) noteEl.textContent = note;
    resultEl.hidden = false;
  }

  function refresh(root) {
    renderChart(root);
    renderResult(root);
  }

  // ---- Body scroll lock (iOS Safari safe) ------------------------------
  var scrollLockCount = 0;
  var scrollLockY = 0;

  function lockBodyScroll() {
    if (scrollLockCount === 0) {
      scrollLockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = (-scrollLockY) + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }
    scrollLockCount++;
  }

  function unlockBodyScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollLockY);
    }
  }

  // ---- Focus trap -------------------------------------------------------
  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusable(panel) {
    return Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) { return el.offsetParent !== null; });
  }

  function trapFocus(root, e) {
    if (e.key !== 'Tab') return;
    var panel = root.querySelector('.soon-dsg__panel');
    if (!panel) return;
    var focusable = getFocusable(panel);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ---- Open / close -----------------------------------------------------
  function openPopup(root, trigger) {
    var state = getState(root);
    state.lastFocused = trigger || document.activeElement;

    refresh(root);
    root.setAttribute('data-open', '');
    root.removeAttribute('hidden');
    lockBodyScroll();

    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    var panel = root.querySelector('.soon-dsg__panel');
    var closeBtn = root.querySelector('[data-soon-dsg-close]');
    (closeBtn || panel || root).focus();
  }

  function closePopup(root) {
    var state = getState(root);
    root.removeAttribute('data-open');
    root.setAttribute('hidden', '');
    unlockBodyScroll();

    var triggers = document.querySelectorAll('[data-soon-dsg-trigger][aria-controls="' + root.id + '"]');
    triggers.forEach && triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    if (!triggers.forEach) {
      // NodeList.forEach is supported in all evergreen browsers; this branch
      // only guards very old browsers where it might be missing.
      for (var i = 0; i < triggers.length; i++) triggers[i].setAttribute('aria-expanded', 'false');
    }

    var toFocus = state.lastFocused;
    if (toFocus && typeof toFocus.focus === 'function') {
      toFocus.focus();
    }
  }

  function findOpenRoot() {
    return document.querySelector('.soon-dsg[data-open]');
  }

  // ---- Delegated event handling ------------------------------------------
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-soon-dsg-trigger]');
    if (trigger) {
      var targetId = trigger.getAttribute('aria-controls');
      var root = targetId ? document.getElementById(targetId) : null;
      if (root) {
        e.preventDefault();
        openPopup(root, trigger);
      }
      return;
    }

    var closeEl = e.target.closest && e.target.closest('[data-soon-dsg-close], [data-soon-dsg-overlay]');
    if (closeEl) {
      var openRoot = closeEl.closest('.soon-dsg');
      if (openRoot) closePopup(openRoot);
      return;
    }

    var unitBtn = e.target.closest && e.target.closest('[data-soon-dsg-unit]');
    if (unitBtn) {
      var unitRoot = unitBtn.closest('.soon-dsg');
      if (!unitRoot) return;
      var st = getState(unitRoot);
      var unit = unitBtn.getAttribute('data-soon-dsg-unit');
      if (unit !== st.unit) {
        st.unit = unit;
        st.submitted = false;
        unitRoot.querySelectorAll('[data-soon-dsg-unit]').forEach(function (b) {
          var active = b === unitBtn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', String(active));
        });
        var label = unitRoot.querySelector('[data-soon-dsg-hip-label]');
        if (label) label.textContent = 'Full hip (' + (unit === 'cm' ? 'cm' : 'inches') + ')';
        var input = unitRoot.querySelector('[data-soon-dsg-hip]');
        if (input) input.placeholder = unit === 'cm' ? 'e.g. 98' : 'e.g. 38.5';
        refresh(unitRoot);
      }
      return;
    }

    var findBtn = e.target.closest && e.target.closest('[data-soon-dsg-find]');
    if (findBtn) {
      var findRoot = findBtn.closest('.soon-dsg');
      if (!findRoot) return;
      getState(findRoot).submitted = true;
      refresh(findRoot);
      return;
    }
  });

  document.addEventListener('input', function (e) {
    if (!e.target.matches || !e.target.matches('[data-soon-dsg-hip]')) return;
    var root = e.target.closest('.soon-dsg');
    if (!root) return;
    // Per spec: changing the input clears the previous submitted result
    // until the customer searches again.
    getState(root).submitted = false;
    renderResult(root);
  });

  document.addEventListener('keydown', function (e) {
    if (e.target.matches && e.target.matches('[data-soon-dsg-hip]') && e.key === 'Enter') {
      var root = e.target.closest('.soon-dsg');
      if (!root) return;
      e.preventDefault();
      getState(root).submitted = true;
      refresh(root);
      return;
    }

    var openRoot = findOpenRoot();
    if (!openRoot) return;

    if (e.key === 'Escape') {
      closePopup(openRoot);
      return;
    }
    trapFocus(openRoot, e);
  });
})();
